import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import ToggleSwitch from 'components/Utils/ToggleSwitch';
import { CardImage } from 'components/Patient/OHIPFields';
import DefaultButton from 'components/Utils/NewDefaultButton';
import './style.scss';
import MiscUtils from 'services/MiscUtils';
import Phone from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';

const OPTIONS = {
  ohip: 'OHIP',
  name: 'Name',
};

const staffRegisterPatientSchema = z.object({
  email: z.string(),
  phone: z.string(),
  typed_ohip: z.string().optional(),
  health_card: z.any().optional(),
  first_name: z.string(),
  last_name: z.string(),
  gender: z.union([z.literal('Male'), z.literal('Female')]).optional(),
  date_of_birth: z.any(),
});
type StaffRegisterPatientValues = z.infer<typeof staffRegisterPatientSchema>;

const StaffRegisterPatient = ({ onSuccess }: { onSuccess: (result: any) => void }) => {
  const dispatch = useAppDispatch();

  const [showHealthCard, setShowHealthCard] = useState(true);
  const [receivedHealthCard, setReceivedHealthCard] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);

  const { control, handleSubmit, setValue, watch } = useForm<StaffRegisterPatientValues>({
    defaultValues: {
      email: '',
      phone: '',
      typed_ohip: '',
      first_name: '',
      last_name: '',
      gender: undefined,
      date_of_birth: undefined,
      health_card: undefined,
    },
  });

  const phone = watch('phone');
  const email = watch('email');
  const typed_ohip = watch('typed_ohip');
  const health_card = watch('health_card');
  console.log('HC', typeof health_card);
  console.log('HCV', typeof receivedHealthCard);
  const first_name = watch('first_name');
  const last_name = watch('last_name');
  const gender = watch('gender');
  const date_of_birth = watch('date_of_birth');

  async function onSubmit(values: StaffRegisterPatientValues) {
    let health_card_value = undefined;

    if (showHealthCard) health_card_value = receivedHealthCard;
    else health_card_value = health_card;

    let payload = {
      email: values.email,
      phone: values.phone,
      first_name: values.first_name,
      last_name: values.last_name,
      gender: values.gender,
      date_of_birth: values.date_of_birth,
      health_card: health_card_value,
    };
    setRegistering(true);
    let result = await dispatch(
      api.Authentication.registerPatientByStaff({ patientByStaffRegisterPayload: payload }),
      {
        errorTitle: 'Register Patient',
      },
    );
    setRegistering(false);
    if (result) {
      onSuccess(result);
    }
  }

  async function onCheckHealthCard(values: StaffRegisterPatientValues) {
    let number = values.typed_ohip;
    if (!number) {
      return;
    }
    setLoading(true);
    let result = await dispatch(
      api.Patients.checkHealthCardNumber({ healthCardNumber: { province: 'Ontario', number } }),
      {
        errorTitle: 'OHIP',
      },
    );
    setLoading(false);
    if (result) {
      setReceivedHealthCard(result);
      if (!result.valid_card) {
        let text = result.response_description + '. ' + result.response_action;
        MiscUtils.showToastrError('OHIP', text);
      }
    }
  }

  function validInputData() {
    return !!first_name && !!last_name && !!date_of_birth && !!gender;
  }

  const mailPhoneValid = !!phone && !!email;
  const showOhipValid =
    !showHealthCard || (showHealthCard && (receivedHealthCard || {}).valid_card);
  const dontShowOhipValid = showHealthCard || (!showHealthCard && validInputData());

  const formValid = mailPhoneValid && showOhipValid && dontShowOhipValid;

  return (
    <div className="staff-register-patient">
      <Phone
        placeholder="Phone number"
        defaultCountry="CA"
        value={phone}
        onChange={((value: string) => setValue('phone', value)) as any}
      />

      <DefaultTextField name="email" control={control} label="Email" />

      <div className="section rounded mt-2">
        <ToggleSwitch
          option1={OPTIONS.ohip}
          option2={OPTIONS.name}
          onChange={(option: string) => {
            setReceivedHealthCard(undefined);
            setValue('health_card', undefined);
            setValue('typed_ohip', undefined);
            setShowHealthCard(option === OPTIONS.ohip);
          }}
          toggled={!showHealthCard}
        />
        {showHealthCard && (
          <>
            <DefaultTextField
              name="typed_ohip"
              control={control}
              label="OHIP"
              style={{ width: 'calc(100% - 150px)' }}
            />
            <DefaultButton
              name="Check"
              style={{ width: '130px', marginLeft: '20px', marginTop: '20px' }}
              onClick={handleSubmit(onCheckHealthCard)}
              disabled={!typed_ohip}
              loading={loading}
            />

            {!!receivedHealthCard && receivedHealthCard.valid_card && (
              <CardImage patient={{ health_card: receivedHealthCard }} />
            )}
          </>
        )}

        {!showHealthCard && (
          <>
            <DefaultTextField name="first_name" control={control} label="First Name" />
            <DefaultTextField name="last_name" control={control} label="Last Name" />
            <DefaultSelectField
              name="gender"
              control={control}
              label="Gender"
              inputs={['Male', 'Female']}
            />
            <DefaultDatePicker name="date_of_birth" control={control} label="Birth Date" />
          </>
        )}
      </div>

      <DefaultButton
        name="Submit"
        className="blue"
        disabled={!formValid}
        onClick={handleSubmit(onSubmit)}
        loading={registering}
      />
    </div>
  );
};

export default StaffRegisterPatient;
